import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useState, useCallback, createContext, useContext, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router";
import { useQueryParams } from "use-query-params";
import { usePrevious } from "react-use";
import { useLocalStorageSynced, useSessionStorage } from "@chronosphereio/core";
import { isAbsoluteTimeRange, isRelativeTimeRange, toAbsoluteTimeRange, getDurationStringSeconds, PinStatus, VAR_QUERY_PARAM_PREFIX } from "../../../core";
import { useCancelInProgressDataQueries } from "../data-querying/cancel-queries";
import { useSetRefreshIntervalParams, useSetTimeRangeParams } from "./query-params";
import { useAnalytics } from "../../model/analytics";
export const TimeRangeContext = /*#__PURE__*/ createContext(undefined);
export function useTimeRangeContext() {
    const ctx = useContext(TimeRangeContext);
    if (ctx === undefined) {
        throw new Error('No TimeRangeContext found. Did you forget a Provider?');
    }
    return ctx;
}
/**
 * Get and set the current resolved time range at runtime.
 */ export function useTimeRange() {
    return useTimeRangeContext();
}
export const PINNED_TIME_RANGE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE';
export const PINNED_TIME_RANGE_ACTIVE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE_ACTIVE';
/**
 * Provider implementation that supplies the time range state at runtime.
 */ export function TimeRangeProvider({ enabledURLParams = false, initialTimeRange, initialRefreshInterval, children, enableTimeRangePinning = false }) {
    const history = useHistory();
    const [queryParams, setQueryParams] = useQueryParams();
    const location = useLocation();
    const previousLocation = usePrevious(location);
    const { timeRange, setTimeRange: setTimeRangeInternal } = useSetTimeRangeParams(initialTimeRange, enabledURLParams);
    const [pinnedTimeRange, setPinnedTimeRangeInternal] = useLocalStorageSynced(PINNED_TIME_RANGE_KEY, {
        value: undefined,
        tabCount: 0,
        backup: undefined
    });
    const [pinnedTimeRangeActive, setPinnedTimeRangeActive] = useSessionStorage(PINNED_TIME_RANGE_ACTIVE_KEY, false);
    const pinStatus = useMemo(()=>{
        if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && pinnedTimeRangeActive) {
            return PinStatus.PINNED;
        } else if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && !pinnedTimeRangeActive) {
            return PinStatus.PINNED_IN_DIFFERENT_TAB;
        } else {
            return PinStatus.UNPINNED;
        }
    }, [
        enableTimeRangePinning,
        pinnedTimeRange.tabCount,
        pinnedTimeRangeActive
    ]);
    const setTimeRange = useCallback((value)=>{
        // if time range is pinned and a new range is selected, update the pin to the new range
        if (pinStatus === PinStatus.PINNED) {
            setPinnedTimeRangeInternal({
                value: value,
                tabCount: pinnedTimeRange.tabCount,
                backup: {
                    value: value,
                    tabCount: pinnedTimeRange.tabCount
                }
            });
        }
        // store the pin value as a backup so we can rehydrate it on page refresh
        setTimeRangeInternal(value, 'pushIn');
    }, [
        pinStatus,
        pinnedTimeRange.tabCount,
        setPinnedTimeRangeInternal,
        setTimeRangeInternal
    ]);
    const setPinnedTimeRange = useCallback((value)=>{
        // store the pin value as a backup so we can rehydrate it on page refresh
        setPinnedTimeRangeInternal({
            ...value,
            backup: value
        });
        // if new tabCount is greater than current, we are pinning in a new tab so set pinnedTimeRangeActive to true
        if (value.tabCount > pinnedTimeRange.tabCount) {
            setPinnedTimeRangeActive(true);
        } else if (value.tabCount < pinnedTimeRange.tabCount) {
            setPinnedTimeRangeActive(false);
        }
    }, [
        pinnedTimeRange.tabCount,
        setPinnedTimeRangeActive,
        setPinnedTimeRangeInternal
    ]);
    const unpinTimeRange = useCallback(()=>{
        setPinnedTimeRange({
            ...pinnedTimeRange,
            tabCount: pinnedTimeRange.tabCount - 1
        });
    }, [
        pinnedTimeRange,
        setPinnedTimeRange
    ]);
    // If the pinned time range changes, update the time range to match
    useEffect(()=>{
        if (pinStatus !== PinStatus.PINNED || !pinnedTimeRange.value) {
            return;
        }
        // When we save the date range to local storage it gets converted to a string so we need to
        // convert it back to Date objects before we can work with it
        const pinnedValue = isAbsoluteTimeRange(pinnedTimeRange.value) ? {
            start: new Date(pinnedTimeRange.value.start),
            end: new Date(pinnedTimeRange.value.end)
        } : pinnedTimeRange.value;
        if (!isEqual(pinnedValue, timeRange)) {
            setTimeRangeInternal(pinnedValue, 'replaceIn');
        }
    // Excluding timeRange from the dependency array to prevent the time range from being overridden when user clicks browser back button
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        enableTimeRangePinning,
        pinStatus,
        pinnedTimeRange,
        setTimeRange,
        setTimeRangeInternal
    ]);
    // Unpin time range if back button is clicked
    useEffect(()=>{
        if (history === undefined) return;
        const unlisten = history.listen((location, action)=>{
            if (action === 'POP') {
                // restore any variable query params to make sure the back button only affects the time range
                if (location.pathname === previousLocation?.pathname) {
                    if (pinStatus === PinStatus.PINNED) {
                        unpinTimeRange();
                    }
                    const varQueryParams = Object.fromEntries(Object.entries(queryParams).filter(([key, value])=>value && key.includes(VAR_QUERY_PARAM_PREFIX)));
                    setQueryParams(varQueryParams, 'replaceIn');
                }
            }
        });
        return ()=>{
            unlisten();
        };
    }, [
        history,
        pinStatus,
        unpinTimeRange,
        queryParams,
        setQueryParams,
        previousLocation
    ]);
    const [refreshKey, setRefreshKey] = useState(0);
    const { cancelInProgressDataQueries } = useCancelInProgressDataQueries();
    const refresh = useCallback(async ()=>{
        await cancelInProgressDataQueries();
        setRefreshKey(refreshKey + 1);
    }, [
        refreshKey,
        cancelInProgressDataQueries
    ]);
    const { refreshInterval, setRefreshInterval, refreshIntervalInMs } = useAutoRefresh({
        refresh,
        initialRefreshInterval,
        enabledURLParams
    });
    const ctx = useMemo(()=>{
        const isRelative = isRelativeTimeRange(timeRange);
        const absoluteTimeRange = isRelative ? toAbsoluteTimeRange(timeRange) : timeRange;
        const rangeMs = absoluteTimeRange.end.valueOf() - absoluteTimeRange.start.valueOf();
        return {
            isRelative,
            timeRange,
            setTimeRange,
            absoluteTimeRange,
            refresh,
            refreshKey: `${absoluteTimeRange.start}:${absoluteTimeRange.end}:${refreshInterval}:${refreshKey}`,
            refreshInterval,
            refreshIntervalInMs,
            setRefreshInterval,
            rangeMs,
            pinnedTimeRange,
            setPinnedTimeRange,
            enableTimeRangePinning,
            pinStatus,
            unpinTimeRange
        };
    }, [
        timeRange,
        setTimeRange,
        refresh,
        refreshInterval,
        refreshKey,
        refreshIntervalInMs,
        setRefreshInterval,
        pinnedTimeRange,
        setPinnedTimeRange,
        enableTimeRangePinning,
        pinStatus,
        unpinTimeRange
    ]);
    // Analytics tracking
    const analytics = useAnalytics();
    useEffect(()=>{
        analytics?.track('time_range_settings', {
            pinStatus: ctx.pinStatus
        });
    }, [
        analytics,
        ctx.pinStatus
    ]);
    return /*#__PURE__*/ _jsx(TimeRangeContext.Provider, {
        value: ctx,
        children: children
    });
}
function useAutoRefresh({ initialRefreshInterval, refresh, enabledURLParams = false }) {
    const { refreshInterval, setRefreshInterval } = useSetRefreshIntervalParams({
        initialRefreshInterval,
        enabledURLParams
    });
    const refreshIntervalInSeconds = refreshInterval ? getDurationStringSeconds(refreshInterval) : null;
    const refreshIntervalInMs = refreshIntervalInSeconds ? refreshIntervalInSeconds * 1000 : null;
    useEffect(()=>{
        if (!refreshIntervalInMs) return;
        const intervalIdx = setInterval(()=>{
            refresh();
        }, refreshIntervalInMs);
        return ()=>{
            clearInterval(intervalIdx);
        };
    }, [
        refreshIntervalInMs,
        refresh
    ]);
    return {
        refreshInterval,
        setRefreshInterval,
        refreshIntervalInMs
    };
}

//# sourceMappingURL=TimeRangeProvider.js.map